import React, { useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Checkbox, Divider, FormControl,
  FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FlipToFrontIcon from '@mui/icons-material/FlipToFront';
import FlipToBackIcon from '@mui/icons-material/FlipToBack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import {
  SoaAlign, SoaBandKey, SoaBorderStyle, SoaColumn, SoaConfig, SoaElement, SoaFieldDef, SoaFieldType, SoaKvRow,
  SoaStyle, SoaSummaryRow, SoaTemplateDefinition, SOA_BAND_LABELS, soaContentWidthMm,
} from '../../types/formattedSoa';
import {
  BAND_KEYS, BAND_SHORT, ELEMENT_LABELS, GROUP_LABELS, GROUP_ORDER, SoaSelection, findElement, fitBandToElements,
  round2, updateColumn, updateElement,
} from '../../utils/soaDesigner';
import { ColorField, InsertFieldButton, NumField, SmallIconButton, StyleEditor, insertAtCaret } from './designerInputs';

type Change = (fn: (d: SoaTemplateDefinition) => SoaTemplateDefinition, mergeKey?: string) => void;

interface Props {
  def: SoaTemplateDefinition;
  selection: SoaSelection;
  onSelect: (s: SoaSelection) => void;
  onChange: Change;
  config: SoaConfig;
  fieldsByKey: Map<string, SoaFieldDef>;
  onDuplicate: () => void;
  onDelete: () => void;
}

const Section: React.FC<{ title: string; defaultExpanded?: boolean; children: React.ReactNode }> = ({ title, defaultExpanded = true, children }) => (
  <Accordion disableGutters defaultExpanded={defaultExpanded} elevation={0} square sx={{ borderBottom: '1px solid #e3e7ec', '&:before': { display: 'none' } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
      <Typography variant="subtitle2">{title}</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0.5 }}>{children}</AccordionDetails>
  </Accordion>
);

function formatsFor(config: SoaConfig, type: SoaFieldType | undefined) {
  return config.formats.filter((f) => !type || f.types.includes(type));
}

const FormatSelect: React.FC<{ config: SoaConfig; type: SoaFieldType | undefined; value: string | undefined; onChange: (v: string | undefined) => void }> = ({
  config, type, value, onChange,
}) => {
  const options = formatsFor(config, type);
  const known = options.some((o) => o.value === (value ?? ''));
  return (
    <FormControl size="small" fullWidth>
      <InputLabel>Format</InputLabel>
      <Select label="Format" value={value ?? ''} onChange={(e) => onChange(e.target.value ? String(e.target.value) : undefined)}>
        {options.map((o) => <MenuItem key={o.value || 'none'} value={o.value}>{o.value ? o.label : 'Default for the field'}</MenuItem>)}
        {!known && value && <MenuItem value={value}>{value}</MenuItem>}
      </Select>
    </FormControl>
  );
};

const AlignToggle: React.FC<{ value: SoaAlign; onChange: (v: SoaAlign) => void }> = ({ value, onChange }) => (
  <ToggleButtonGroup size="small" exclusive value={value} aria-label="Alignment" onChange={(_, v: SoaAlign | null) => { if (v) onChange(v); }}>
    <ToggleButton value="left">Left</ToggleButton>
    <ToggleButton value="center">Centre</ToggleButton>
    <ToggleButton value="right">Right</ToggleButton>
  </ToggleButtonGroup>
);

// ── element ──────────────────────────────────────────────────────────────────────────────────

const ElementProperties: React.FC<Props & { band: SoaBandKey; el: SoaElement }> = ({
  band, el, onSelect, onChange, config, fieldsByKey, onDuplicate, onDelete,
}) => {
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const id = el.id;
  const set = (fn: (e: SoaElement) => SoaElement, prop: string) => onChange((d) => fitBandToElements(updateElement(d, band, id, fn), band), `el-${id}-${prop}`);
  const style = el.style ?? {};
  const setStyle = (s: SoaStyle) => set((e) => ({ ...e, style: s }), 'style');
  const elementFields = config.fields
    .filter((f) => f.group !== 'row' && f.type !== 'image')
    .sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));

  const moveToBand = (target: SoaBandKey) => {
    if (target === band) return;
    onChange((d) => {
      const e = findElement(d, band, id);
      if (!e) return d;
      const without = { ...d, bands: { ...d.bands, [band]: { ...d.bands[band], elements: d.bands[band].elements.filter((x) => x.id !== id) } } };
      const y = Math.max(0, Math.min(e.y, without.bands[target].height - e.h));
      const moved = { ...without, bands: { ...without.bands, [target]: { ...without.bands[target], elements: [...without.bands[target].elements, { ...e, y }] } } };
      return fitBandToElements(moved, target);
    });
    onSelect({ kind: 'element', band: target, id });
  };

  const reorder = (toFront: boolean) => onChange((d) => {
    const list = d.bands[band].elements;
    const e = list.find((x) => x.id === id);
    if (!e) return d;
    const rest = list.filter((x) => x.id !== id);
    return { ...d, bands: { ...d.bands, [band]: { ...d.bands[band], elements: toFront ? [...rest, e] : [e, ...rest] } } };
  });

  const onImage = (file: File | undefined) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) { setMessage('Choose a PNG, JPG, GIF or WebP image.'); return; }
    if (file.size > 2 * 1024 * 1024) { setMessage('The image is larger than 2 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setMessage(null);
      set((e) => (e.type === 'image' ? { ...e, source: 'custom', dataUrl: String(reader.result) } : e), 'image');
    };
    reader.readAsDataURL(file);
  };

  const grows = el.type === 'text' || el.type === 'field' || el.type === 'kv' || el.type === 'html';

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>{ELEMENT_LABELS[el.type]}</Typography>
        <Stack direction="row">
          <SmallIconButton label="Bring to front" onClick={() => reorder(true)}><FlipToFrontIcon fontSize="small" /></SmallIconButton>
          <SmallIconButton label="Send to back" onClick={() => reorder(false)}><FlipToBackIcon fontSize="small" /></SmallIconButton>
          <SmallIconButton label="Duplicate (Ctrl+D)" onClick={onDuplicate}><ContentCopyIcon fontSize="small" /></SmallIconButton>
          <SmallIconButton label="Delete (Del)" onClick={onDelete} color="error"><DeleteOutlineIcon fontSize="small" /></SmallIconButton>
        </Stack>
      </Stack>
      {message && <Alert severity="warning" onClose={() => setMessage(null)} sx={{ mx: 1.5, mb: 1 }}>{message}</Alert>}

      <Section title="Position">
        <Stack spacing={1.25}>
          <TextField size="small" label="Name (designer only)" value={el.name ?? ''} onChange={(e) => set((x) => ({ ...x, name: e.target.value || undefined }), 'name')} />
          <Stack direction="row" spacing={1}>
            <NumField label="X" unit="mm" value={el.x} min={-50} max={1000} width={86} onChange={(v) => set((x) => ({ ...x, x: v ?? 0 }), 'x')} />
            <NumField label="Y" unit="mm" value={el.y} min={0} max={1000} width={86} onChange={(v) => set((x) => ({ ...x, y: v ?? 0 }), 'y')} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <NumField label="Width" unit="mm" value={el.w} min={0.5} max={1000} width={86} onChange={(v) => set((x) => ({ ...x, w: v ?? 1 }), 'w')} />
            <NumField label="Height" unit="mm" value={el.h} min={0.2} max={1000} width={86} onChange={(v) => set((x) => ({ ...x, h: v ?? 1 }), 'h')} />
          </Stack>
          <FormControl size="small">
            <InputLabel>Band</InputLabel>
            <Select label="Band" value={band} onChange={(e) => moveToBand(e.target.value as SoaBandKey)}>
              {BAND_KEYS.map((k) => <MenuItem key={k} value={k}>{SOA_BAND_LABELS[k]}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Section>

      <Section title="Content">
        {el.type === 'text' && (
          <Stack spacing={0.75}>
            <TextField
              size="small" multiline minRows={3} maxRows={14} label="Text" value={el.text} inputRef={textRef}
              onChange={(e) => set((x) => (x.type === 'text' ? { ...x, text: e.target.value } : x), 'text')}
            />
            <InsertFieldButton fields={config.fields} formats={config.formats}
              onInsert={(t) => set((x) => (x.type === 'text' ? { ...x, text: insertAtCaret(textRef.current, x.text, t) } : x), 'text-insert')} />
            <Typography variant="caption" color="text.secondary">
              {'{{field|format}}'} prints a value. A line whose fields are all empty is left out.
            </Typography>
          </Stack>
        )}
        {el.type === 'field' && (
          <Stack spacing={1.25}>
            <Autocomplete
              size="small"
              options={elementFields}
              groupBy={(o) => GROUP_LABELS[o.group] ?? o.group}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              value={fieldsByKey.get(el.field) ?? null}
              onChange={(_, v) => { if (v) set((x) => (x.type === 'field' ? { ...x, field: v.key, format: undefined } : x), 'field'); }}
              renderInput={(p) => <TextField {...p} label="Field" />}
            />
            <FormatSelect config={config} type={fieldsByKey.get(el.field)?.type} value={el.format}
              onChange={(v) => set((x) => (x.type === 'field' ? { ...x, format: v } : x), 'format')} />
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Before" value={el.prefix ?? ''} onChange={(e) => set((x) => (x.type === 'field' ? { ...x, prefix: e.target.value || undefined } : x), 'prefix')} />
              <TextField size="small" label="After" value={el.suffix ?? ''} onChange={(e) => set((x) => (x.type === 'field' ? { ...x, suffix: e.target.value || undefined } : x), 'suffix')} />
            </Stack>
          </Stack>
        )}
        {el.type === 'image' && (
          <Stack spacing={1}>
            <RadioGroup value={el.source} onChange={(e) => set((x) => (x.type === 'image' ? { ...x, source: e.target.value as 'company.logo' | 'custom' } : x), 'source')}>
              <FormControlLabel value="company.logo" control={<Radio size="small" />} label="Company logo (Company Management › Report settings)" />
              <FormControlLabel value="custom" control={<Radio size="small" />} label="An image of this template" />
            </RadioGroup>
            {el.source === 'custom' && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" variant="outlined" component="label" startIcon={<UploadIcon />}>
                  Choose image
                  <input hidden type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={(e) => { onImage(e.target.files?.[0]); e.target.value = ''; }} />
                </Button>
                {el.dataUrl && <img src={el.dataUrl} alt="Chosen" style={{ maxHeight: 40, maxWidth: 120, border: '1px solid #e0e0e0' }} />}
              </Stack>
            )}
            <FormControl size="small">
              <InputLabel>Fit</InputLabel>
              <Select label="Fit" value={el.fit ?? 'contain'} onChange={(e) => set((x) => (x.type === 'image' ? { ...x, fit: e.target.value as 'contain' | 'cover' | 'fill' } : x), 'fit')}>
                <MenuItem value="contain">Whole image (contain)</MenuItem>
                <MenuItem value="cover">Fill the box, cropped (cover)</MenuItem>
                <MenuItem value="fill">Stretch (fill)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}
        {el.type === 'line' && (
          <Stack spacing={1.25}>
            <ToggleButtonGroup size="small" exclusive value={el.orientation} aria-label="Orientation"
              onChange={(_, v: 'horizontal' | 'vertical' | null) => {
                if (!v || v === el.orientation) return;
                set((x) => (x.type === 'line' ? { ...x, orientation: v, w: v === 'vertical' ? 0.5 : x.h, h: v === 'vertical' ? x.w : 0.5 } : x), 'orientation');
              }}>
              <ToggleButton value="horizontal">Horizontal</ToggleButton>
              <ToggleButton value="vertical">Vertical</ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row" spacing={1} alignItems="center">
              <NumField label="Thickness" unit="pt" value={el.thickness} min={0.1} max={10} step={0.25} width={120}
                onChange={(v) => set((x) => (x.type === 'line' ? { ...x, thickness: v ?? 0.75 } : x), 'thickness')} />
              <ColorField label="Colour" value={el.color} onChange={(v) => set((x) => (x.type === 'line' ? { ...x, color: v || '#000000' } : x), 'color')} />
            </Stack>
            <FormControl size="small">
              <InputLabel>Style</InputLabel>
              <Select label="Style" value={el.lineStyle} onChange={(e) => set((x) => (x.type === 'line' ? { ...x, lineStyle: e.target.value as SoaBorderStyle } : x), 'lineStyle')}>
                {(['solid', 'dashed', 'dotted', 'double'] as SoaBorderStyle[]).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        )}
        {el.type === 'box' && (
          <NumField label="Corner radius" unit="mm" value={el.radius ?? 0} min={0} max={50} width={140}
            onChange={(v) => set((x) => (x.type === 'box' ? { ...x, radius: v ?? 0 } : x), 'radius')} />
        )}
        {el.type === 'kv' && <KvEditor el={el} config={config} onChange={(fn, prop) => set((x) => (x.type === 'kv' ? fn(x) : x), prop)} />}
        {el.type === 'html' && (
          <Stack spacing={0.75}>
            <TextField
              size="small" multiline minRows={5} maxRows={18} label="HTML" value={el.html} inputRef={textRef}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
              onChange={(e) => set((x) => (x.type === 'html' ? { ...x, html: e.target.value } : x), 'html')}
            />
            <InsertFieldButton fields={config.fields} formats={config.formats}
              onInsert={(t) => set((x) => (x.type === 'html' ? { ...x, html: insertAtCaret(textRef.current, x.html, t) } : x), 'html-insert')} />
            <Typography variant="caption" color="text.secondary">
              Field values are inserted escaped. Scripts in HTML do not run: JavaScript goes in the Code tab.
            </Typography>
          </Stack>
        )}
      </Section>

      {el.type !== 'line' && (
        <Section title={el.type === 'kv' ? 'Style (both columns)' : 'Style'} defaultExpanded={el.type !== 'kv'}>
          <StyleEditor value={style} onChange={setStyle} fonts={config.fonts} mode={el.type === 'box' || el.type === 'image' ? 'box' : 'text'} />
        </Section>
      )}

      {(grows || el.type === 'image') && (
        <Section title="Behaviour">
          <Stack>
            {grows && (
              <FormControlLabel
                control={<Checkbox size="small" checked={!!el.canGrow} onChange={(e) => set((x) => ({ ...x, canGrow: e.target.checked }), 'canGrow')} />}
                label="Can grow (taller with its content; pushes what is below it down)"
              />
            )}
            <FormControlLabel
              control={<Checkbox size="small" checked={!!el.hideIfEmpty} onChange={(e) => set((x) => ({ ...x, hideIfEmpty: e.target.checked }), 'hideIfEmpty')} />}
              label="Hide when empty"
            />
          </Stack>
        </Section>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1.5 }}>
        Id {el.id} · CSS: [data-el=&quot;{el.id}&quot;] · in {BAND_SHORT[band]}
      </Typography>
    </Box>
  );
};

const KvEditor: React.FC<{
  el: Extract<SoaElement, { type: 'kv' }>;
  config: SoaConfig;
  onChange: (fn: (e: Extract<SoaElement, { type: 'kv' }>) => Extract<SoaElement, { type: 'kv' }>, prop: string) => void;
}> = ({ el, config, onChange }) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const setRows = (rows: SoaKvRow[], prop: string) => onChange((e) => ({ ...e, rows }), prop);
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= el.rows.length) return;
    const rows = [...el.rows];
    [rows[i], rows[j]] = [rows[j], rows[i]];
    setRows(rows, 'rows-move');
  };
  return (
    <Stack spacing={1.25}>
      {el.rows.map((r, i) => (
        <Box key={i} sx={{ border: '1px solid #e3e7ec', borderRadius: 1, p: 1 }}>
          <Stack spacing={0.75}>
            <TextField size="small" label={`Label ${i + 1}`} value={r.label}
              onChange={(e) => setRows(el.rows.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)), `row${i}-label`)} />
            <TextField size="small" label="Value" value={r.value} inputRef={(n) => { refs.current[i] = n; }}
              onChange={(e) => setRows(el.rows.map((x, k) => (k === i ? { ...x, value: e.target.value } : x)), `row${i}-value`)} />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <InsertFieldButton fields={config.fields} formats={config.formats}
                onInsert={(t) => setRows(el.rows.map((x, k) => (k === i ? { ...x, value: insertAtCaret(refs.current[i], x.value, t) } : x)), `row${i}-insert`)} />
              <Stack direction="row">
                <SmallIconButton label="Move up" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUpwardIcon fontSize="small" /></SmallIconButton>
                <SmallIconButton label="Move down" onClick={() => move(i, 1)} disabled={i === el.rows.length - 1}><ArrowDownwardIcon fontSize="small" /></SmallIconButton>
                <SmallIconButton label="Remove row" color="error" onClick={() => setRows(el.rows.filter((_, k) => k !== i), 'rows-remove')}><DeleteOutlineIcon fontSize="small" /></SmallIconButton>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} disabled={el.rows.length >= 30}
        onClick={() => setRows([...el.rows, { label: 'LABEL', value: '' }], 'rows-add')}>Add row</Button>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <NumField label="Label width" unit="%" value={el.labelWidth} min={5} max={95} step={1} width={120} onChange={(v) => onChange((e) => ({ ...e, labelWidth: v ?? 30 }), 'labelWidth')} />
        <NumField label="Grid" unit="pt" value={el.gridWidth} min={0} max={5} step={0.25} width={90} onChange={(v) => onChange((e) => ({ ...e, gridWidth: v ?? 0 }), 'gridWidth')} />
        <ColorField label="Grid" value={el.gridColor} onChange={(v) => onChange((e) => ({ ...e, gridColor: v || '#b9b9b9' }), 'gridColor')} />
      </Stack>
      <Divider />
      <Typography variant="subtitle2">Label column</Typography>
      <StyleEditor value={el.labelStyle} onChange={(s) => onChange((e) => ({ ...e, labelStyle: s }), 'labelStyle')} fonts={config.fonts} />
      <Divider />
      <Typography variant="subtitle2">Value column</Typography>
      <StyleEditor value={el.valueStyle} onChange={(s) => onChange((e) => ({ ...e, valueStyle: s }), 'valueStyle')} fonts={config.fonts} />
    </Stack>
  );
};

// ── column ───────────────────────────────────────────────────────────────────────────────────

const ColumnProperties: React.FC<Props & { col: SoaColumn }> = ({ def, col, onChange, onSelect, config, fieldsByKey }) => {
  const set = (fn: (c: SoaColumn) => SoaColumn, prop: string) => onChange((d) => updateColumn(d, col.id, fn), `col-${col.id}-${prop}`);
  const rowFields = config.fields.filter((f) => f.group === 'row');
  const index = def.table.columns.findIndex((c) => c.id === col.id);
  const move = (delta: number) => onChange((d) => {
    const cols = [...d.table.columns];
    const i = cols.findIndex((c) => c.id === col.id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= cols.length) return d;
    [cols[i], cols[j]] = [cols[j], cols[i]];
    return { ...d, table: { ...d.table, columns: cols } };
  });
  const remove = () => {
    if (def.table.columns.length <= 1) return;
    onChange((d) => {
      const cols = d.table.columns.filter((c) => c.id !== col.id);
      const fix = (r: SoaSummaryRow) => (r.labelColumn === col.id ? { ...r, labelColumn: cols[0].id } : r);
      return { ...d, table: { ...d.table, columns: cols, openingRow: fix(d.table.openingRow), closingRow: { ...d.table.closingRow, ...fix(d.table.closingRow) } } };
    });
    onSelect({ kind: 'table' });
  };
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>Column {index + 1}</Typography>
        <Stack direction="row">
          <SmallIconButton label="Move left" onClick={() => move(-1)} disabled={index <= 0}><ArrowBackIcon fontSize="small" /></SmallIconButton>
          <SmallIconButton label="Move right" onClick={() => move(1)} disabled={index >= def.table.columns.length - 1}><ArrowForwardIcon fontSize="small" /></SmallIconButton>
          <SmallIconButton label="Remove column" color="error" onClick={remove} disabled={def.table.columns.length <= 1}><DeleteOutlineIcon fontSize="small" /></SmallIconButton>
        </Stack>
      </Stack>
      <Section title="Column">
        <Stack spacing={1.25}>
          <TextField size="small" label="Heading" value={col.header} onChange={(e) => set((c) => ({ ...c, header: e.target.value }), 'header')} />
          <Autocomplete
            size="small"
            options={rowFields}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.key === b.key}
            value={fieldsByKey.get(col.field) ?? null}
            onChange={(_, v) => { if (v) set((c) => ({ ...c, field: v.key, format: undefined }), 'field'); }}
            renderInput={(p) => <TextField {...p} label="Field" />}
          />
          <FormatSelect config={config} type={fieldsByKey.get(col.field)?.type} value={col.format} onChange={(v) => set((c) => ({ ...c, format: v }), 'format')} />
          <Stack direction="row" spacing={1} alignItems="center">
            <NumField label="Width" unit="mm" value={col.width} min={3} max={500} width={100} onChange={(v) => set((c) => ({ ...c, width: v ?? 20 }), 'width')} />
            <AlignToggle value={col.align === 'justify' ? 'left' : col.align} onChange={(v) => set((c) => ({ ...c, align: v }), 'align')} />
          </Stack>
          <FormControlLabel
            control={<Checkbox size="small" checked={col.wrap} onChange={(e) => set((c) => ({ ...c, wrap: e.target.checked }), 'wrap')} />}
            label="Wrap onto more lines (otherwise one line, shrunk to fit)"
          />
          {col.field === 'docType' && (
            <Typography variant="caption" color="text.secondary">The codes per document type are set on the Settings tab.</Typography>
          )}
        </Stack>
      </Section>
      <Section title="Cells (over the table style)" defaultExpanded={false}>
        <StyleEditor value={col.style ?? {}} onChange={(s) => set((c) => ({ ...c, style: Object.keys(s).length ? s : undefined }), 'style')} fonts={config.fonts} inheritLabel="Table's" />
      </Section>
      <Section title="Heading (over the table style)" defaultExpanded={false}>
        <StyleEditor value={col.headerStyle ?? {}} onChange={(s) => set((c) => ({ ...c, headerStyle: Object.keys(s).length ? s : undefined }), 'headerStyle')} fonts={config.fonts} inheritLabel="Table's" />
      </Section>
    </Box>
  );
};

// ── band ─────────────────────────────────────────────────────────────────────────────────────

const BAND_HELP: Record<SoaBandKey, string> = {
  pageHeader: 'Printed at the top of every page: logo, company details, title, period.',
  reportHeader: 'Printed once, on the first page of each statement, under the page header: the account box.',
  reportFooter: 'Printed once, after the table: closing notes, signatures. Moves to a new page when it does not fit.',
  pageFooter: 'Printed at the bottom of every page: page numbers, printed by.',
};

const BandProperties: React.FC<Props & { band: SoaBandKey }> = ({ def, band, onChange }) => {
  const b = def.bands[band];
  const set = (patch: Partial<typeof b>, prop: string) => onChange((d) => ({ ...d, bands: { ...d.bands, [band]: { ...d.bands[band], ...patch } } }), `band-${band}-${prop}`);
  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle1" fontWeight={600}>{SOA_BAND_LABELS[band]}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{BAND_HELP[band]}</Typography>
      <Stack spacing={1.5}>
        <NumField label="Height" unit="mm" value={b.height} min={0} max={500} width={130} onChange={(v) => set({ height: v ?? 0 }, 'height')}
          helperText="Grows on paper when its content needs more" />
        <FormControlLabel control={<Checkbox size="small" checked={b.visible} onChange={(e) => set({ visible: e.target.checked }, 'visible')} />} label="Printed" />
        <ColorField label="Background" value={b.background || undefined} optional fallback="#f5f5f5" onChange={(v) => set({ background: v }, 'background')} />
        <Typography variant="caption" color="text.secondary">{b.elements.length} element{b.elements.length === 1 ? '' : 's'}. Add more with the toolbar or the field list.</Typography>
      </Stack>
    </Box>
  );
};

// ── table ────────────────────────────────────────────────────────────────────────────────────

const SummaryRowEditor: React.FC<{
  title: string; row: SoaSummaryRow; columns: SoaColumn[]; fonts: string[]; onChange: (r: SoaSummaryRow, prop: string) => void; extra?: React.ReactNode;
}> = ({ title, row, columns, fonts, onChange, extra }) => (
  <Section title={title} defaultExpanded={false}>
    <Stack spacing={1.25}>
      <FormControlLabel control={<Checkbox size="small" checked={row.show} onChange={(e) => onChange({ ...row, show: e.target.checked }, 'show')} />} label="Printed" />
      <TextField size="small" label="Label" value={row.label} onChange={(e) => onChange({ ...row, label: e.target.value }, 'label')} />
      <FormControl size="small">
        <InputLabel>Label in column</InputLabel>
        <Select label="Label in column" value={columns.some((c) => c.id === row.labelColumn) ? row.labelColumn : ''} onChange={(e) => onChange({ ...row, labelColumn: String(e.target.value) }, 'labelColumn')}>
          {columns.map((c) => <MenuItem key={c.id} value={c.id}>{c.header || c.field}</MenuItem>)}
        </Select>
      </FormControl>
      {extra}
      <Typography variant="caption" color="text.secondary">Row style</Typography>
      <StyleEditor value={row.style} onChange={(s) => onChange({ ...row, style: s }, 'style')} fonts={fonts} inheritLabel="Table's" />
    </Stack>
  </Section>
);

const TableProperties: React.FC<Props> = ({ def, onChange, config }) => {
  const t = def.table;
  const set = (patch: Partial<typeof t>, prop: string) => onChange((d) => ({ ...d, table: { ...d.table, ...patch } }), `table-${prop}`);
  const contentW = soaContentWidthMm(def);
  const total = round2(t.columns.reduce((s, c) => s + c.width, 0));
  const fit = () => onChange((d) => {
    const sum = d.table.columns.reduce((s, c) => s + c.width, 0) || 1;
    const cols = d.table.columns.map((c) => ({ ...c, width: round2((c.width / sum) * contentW) }));
    const diff = round2(contentW - cols.reduce((s, c) => s + c.width, 0));
    if (cols.length) cols[cols.length - 1] = { ...cols[cols.length - 1], width: round2(cols[cols.length - 1].width + diff) };
    return { ...d, table: { ...d.table, columns: cols } };
  });
  return (
    <Box>
      <Box sx={{ p: 1.5, pb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>Details table</Typography>
        <Typography variant="body2" color="text.secondary">
          One row per journal line in the period. It runs across pages; the headings repeat and a row is never cut.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="caption">Columns {total} mm, table {round2(contentW)} mm</Typography>
          <Button size="small" onClick={fit} disabled={Math.abs(total - contentW) < 0.05}>Fit to width</Button>
        </Stack>
      </Box>
      <Section title="Lines and colours">
        <Stack spacing={1.25}>
          <ColorField label="Every other row" value={t.zebra || undefined} optional fallback="#f2f2f2" onChange={(v) => set({ zebra: v ?? '' }, 'zebra')} />
          <Stack direction="row" spacing={1} alignItems="center">
            <NumField label="Grid" unit="pt" value={t.gridWidth} min={0} max={5} step={0.25} width={96} onChange={(v) => set({ gridWidth: v ?? 0 }, 'gridWidth')} />
            <ColorField label="Grid" value={t.gridColor} onChange={(v) => set({ gridColor: v || '#a9c4de' }, 'gridColor')} />
          </Stack>
          <Stack>
            <FormControlLabel control={<Checkbox size="small" checked={t.verticalLines} onChange={(e) => set({ verticalLines: e.target.checked }, 'v')} />} label="Lines between columns" />
            <FormControlLabel control={<Checkbox size="small" checked={t.horizontalLines} onChange={(e) => set({ horizontalLines: e.target.checked }, 'h')} />} label="Lines between rows" />
            <FormControlLabel control={<Checkbox size="small" checked={t.outerBorder} onChange={(e) => set({ outerBorder: e.target.checked }, 'outer')} />} label="Outer border" />
            <FormControlLabel control={<Checkbox size="small" checked={t.repeatHeader} onChange={(e) => set({ repeatHeader: e.target.checked }, 'repeat')} />} label="Repeat the headings on every page" />
          </Stack>
          <Stack direction="row" spacing={1}>
            <NumField label="Space before" unit="mm" value={t.spaceBefore} min={0} max={100} width={130} onChange={(v) => set({ spaceBefore: v ?? 0 }, 'spaceBefore')} />
            <NumField label="Space after" unit="mm" value={t.spaceAfter} min={0} max={100} width={130} onChange={(v) => set({ spaceAfter: v ?? 0 }, 'spaceAfter')} />
          </Stack>
          <TextField size="small" label="Text when there are no transactions" value={t.emptyText} onChange={(e) => set({ emptyText: e.target.value }, 'emptyText')} />
        </Stack>
      </Section>
      <Section title="Headings style" defaultExpanded={false}>
        <StyleEditor value={t.headerStyle} onChange={(s) => set({ headerStyle: s }, 'headerStyle')} fonts={config.fonts} />
      </Section>
      <Section title="Cells style" defaultExpanded={false}>
        <StyleEditor value={t.cellStyle} onChange={(s) => set({ cellStyle: s }, 'cellStyle')} fonts={config.fonts} />
      </Section>
      <SummaryRowEditor title="Opening balance row" row={t.openingRow} columns={t.columns} fonts={config.fonts}
        onChange={(r, prop) => set({ openingRow: r }, `opening-${prop}`)} />
      <SummaryRowEditor
        title="Totals row (closing balance)"
        row={t.closingRow}
        columns={t.columns}
        fonts={config.fonts}
        onChange={(r, prop) => set({ closingRow: { ...t.closingRow, ...r } }, `closing-${prop}`)}
        extra={(
          <FormControlLabel
            control={<Checkbox size="small" checked={t.closingRow.showTotals} onChange={(e) => set({ closingRow: { ...t.closingRow, showTotals: e.target.checked } }, 'closing-totals')} />}
            label="Show the period's debit and credit totals"
          />
        )}
      />
    </Box>
  );
};

// ── panel ────────────────────────────────────────────────────────────────────────────────────

const PropertiesPanel: React.FC<Props> = (props) => {
  const { def, selection } = props;
  if (selection?.kind === 'element') {
    const el = findElement(def, selection.band, selection.id);
    if (el) return <ElementProperties key={el.id} {...props} band={selection.band} el={el} />;
  }
  if (selection?.kind === 'column') {
    const col = def.table.columns.find((c) => c.id === selection.id);
    if (col) return <ColumnProperties key={col.id} {...props} col={col} />;
  }
  if (selection?.kind === 'band') return <BandProperties key={selection.band} {...props} band={selection.band} />;
  if (selection?.kind === 'table') return <TableProperties {...props} />;
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>Properties</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select an element, a column heading, a band name or the table to change it.
      </Typography>
      <Typography variant="subtitle2">Keyboard</Typography>
      <Typography component="div" variant="body2" color="text.secondary">
        <ul style={{ paddingLeft: 18, margin: '4px 0' }}>
          <li>Arrows: move 0.5 mm (Shift: 5 mm)</li>
          <li>Delete: remove · Ctrl+D: duplicate</li>
          <li>Ctrl+C / Ctrl+V: copy and paste</li>
          <li>Ctrl+Z / Ctrl+Y: undo and redo · Ctrl+S: save</li>
          <li>Esc: clear the selection · Shift-drag: keep the axis</li>
        </ul>
      </Typography>
    </Box>
  );
};

export default PropertiesPanel;
